import { useState, useRef } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  UploadCloud, Loader2, FileJson, X, CheckCircle2, XCircle, Clock, GitCompare,
} from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;
const authHdr = (token) => ({ Authorization: `Bearer ${token}` });

const STATUS_STYLE = {
  pending:    { label: "Pending",    className: "text-slate-500 border-slate-300 bg-slate-50" },
  validating: { label: "Validating", className: "text-sky-700 border-sky-300 bg-sky-50" },
  validated:  { label: "Validated",  className: "text-teal-700 border-teal-300 bg-teal-50" },
  committing: { label: "Committing", className: "text-sky-700 border-sky-300 bg-sky-50" },
  committed:  { label: "Committed",  className: "text-emerald-700 border-emerald-300 bg-emerald-50" },
  failed:     { label: "Failed",     className: "text-red-700 border-red-300 bg-red-50" },
};

function StatusChip({ status }) {
  const s = STATUS_STYLE[status] || STATUS_STYLE.pending;
  const spinning = status === "validating" || status === "committing";
  return (
    <Badge variant="outline" className={`text-[10px] ${s.className}`} data-testid={`queue-item-status-${status}`}>
      {spinning ? <Loader2 className="w-2.5 h-2.5 mr-1 animate-spin inline" /> : null}
      {s.label}
    </Badge>
  );
}

function formatSize(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

let _nextId = 1;

export default function UploadQueue({ token, onCommitted }) {
  const [items, setItems] = useState([]);
  const [phase, setPhase] = useState("idle"); // idle | validating | review | committing | done
  const [dragOver, setDragOver] = useState(false);
  const [sourceBoard, setSourceBoard] = useState("NCERT");
  const [sourceSchool, setSourceSchool] = useState("");
  const [isShared, setIsShared] = useState(true);
  const fileInputRef = useRef(null);

  const patchItem = (id, patch) => setItems((prev) => prev.map((it) => (it.id === id ? { ...it, ...patch } : it)));

  const addFiles = (fileList) => {
    const jsonFiles = Array.from(fileList).filter((f) => f.name.toLowerCase().endsWith(".json"));
    if (!jsonFiles.length) { toast.error("Only .json files are accepted"); return; }
    const newItems = jsonFiles.map((file) => ({
      id: _nextId++, file, filename: file.name, size: file.size,
      status: "pending", batch_id: null, diff: null, validationErrors: 0, error: null,
    }));
    setItems((prev) => [...prev, ...newItems]);
  };

  const removeItem = (id) => setItems((prev) => prev.filter((it) => it.id !== id));

  const resetQueue = () => { setItems([]); setPhase("idle"); };

  const handleDrop = (e) => {
    e.preventDefault(); setDragOver(false);
    if (e.dataTransfer.files?.length) addFiles(e.dataTransfer.files);
  };

  const validateAll = async () => {
    setPhase("validating");
    for (const item of items) {
      patchItem(item.id, { status: "validating" });
      try {
        const form = new FormData();
        form.append("file", item.file);
        const res = await fetch(`${API}/api/admin/cr/dataset-ops/upload-validate`, {
          method: "POST", headers: authHdr(token), body: form,
        });
        const json = await res.json();
        if (!res.ok) { patchItem(item.id, { status: "failed", error: json.detail || "Validation failed" }); continue; }
        if (json.validation.errors > 0) {
          patchItem(item.id, { status: "failed", error: `${json.validation.errors} validation error(s)`, validationErrors: json.validation.errors });
          continue;
        }
        const diffRes = await fetch(`${API}/api/admin/cr/dataset-ops/diff/${json.batch_id}`, { headers: authHdr(token) });
        const diffJson = await diffRes.json();
        if (!diffRes.ok) { patchItem(item.id, { status: "failed", error: diffJson.detail || "Diff failed" }); continue; }
        patchItem(item.id, { status: "validated", batch_id: json.batch_id, diff: diffJson });
      } catch {
        patchItem(item.id, { status: "failed", error: "Network error" });
      }
    }
    setPhase("review");
  };

  const commitAll = async () => {
    setPhase("committing");
    for (const item of items) {
      if (item.status !== "validated") continue;
      patchItem(item.id, { status: "committing" });
      try {
        const res = await fetch(`${API}/api/admin/cr/dataset-ops/commit/${item.batch_id}`, {
          method: "POST", headers: { ...authHdr(token), "Content-Type": "application/json" },
          body: JSON.stringify({ source_board: sourceBoard, source_school: sourceSchool || null, is_shared: isShared }),
        });
        const json = await res.json();
        if (!res.ok) { patchItem(item.id, { status: "failed", error: json.detail || "Commit failed" }); continue; }
        patchItem(item.id, { status: "committed" });
      } catch {
        patchItem(item.id, { status: "failed", error: "Network error during commit" });
      }
    }
    setPhase("done");
    onCommitted?.();
  };

  const combinedDiff = items.filter((i) => i.diff).reduce((acc, i) => ({
    new_count: acc.new_count + i.diff.new_count,
    updated_count: acc.updated_count + i.diff.updated_count,
    unchanged_count: acc.unchanged_count + i.diff.unchanged_count,
  }), { new_count: 0, updated_count: 0, unchanged_count: 0 });

  const failedCount = items.filter((i) => i.status === "failed").length;
  const committedCount = items.filter((i) => i.status === "committed").length;
  const readyCount = items.filter((i) => i.status === "validated").length;

  return (
    <Card className="p-5 border border-slate-200 shadow-none" data-testid="upload-queue-card">
      <h3 className="font-semibold text-slate-800 mb-1">Upload + Validate</h3>
      <p className="text-xs text-slate-500 mb-4">Drop multiple dataset JSON files, or click to browse. Each file is validated then committed one at a time — no writes to fuel_questions until you confirm.</p>

      {phase === "idle" && (
        <div
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
          className={`border-2 border-dashed rounded-xl py-8 flex flex-col items-center gap-2 cursor-pointer transition-colors ${
            dragOver ? "border-sky-400 bg-sky-50" : "border-slate-300 bg-slate-50/50 hover:border-slate-400"
          }`}
          data-testid="dataset-dropzone"
        >
          <UploadCloud className="w-7 h-7 text-slate-400" />
          <p className="text-sm text-slate-600">Drag &amp; drop JSON files here, or click to browse</p>
          <p className="text-xs text-slate-400">Multiple files supported</p>
          <input
            ref={fileInputRef} type="file" accept=".json" multiple className="hidden"
            onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }}
            data-testid="dataset-file-input"
          />
        </div>
      )}

      {items.length > 0 && (
        <div className="mt-4 space-y-1.5" data-testid="dataset-file-queue">
          {items.map((it) => (
            <div key={it.id} className="flex items-center justify-between gap-2 text-xs bg-slate-50 border border-slate-100 rounded-md px-3 py-2" data-testid={`file-queue-item-${it.filename}`}>
              <div className="flex items-center gap-2 min-w-0">
                <FileJson className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                <span className="font-medium text-slate-700 truncate">{it.filename}</span>
                <span className="text-slate-400 shrink-0">{formatSize(it.size)}</span>
                {it.diff && (
                  <span className="text-slate-400 shrink-0 flex items-center gap-1">
                    <GitCompare className="w-3 h-3" /> {it.diff.new_count} new, {it.diff.updated_count} updated
                  </span>
                )}
                {it.error && <span className="text-red-600 truncate">{it.error}</span>}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <StatusChip status={it.status} />
                {phase === "idle" && (
                  <button onClick={() => removeItem(it.id)} data-testid={`remove-queue-item-${it.filename}`}>
                    <X className="w-3.5 h-3.5 text-slate-400 hover:text-red-500" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {items.length > 0 && phase === "idle" && (
        <div className="mt-4">
          <Button onClick={validateAll} data-testid="validate-all-btn">
            <CheckCircle2 className="w-4 h-4 mr-1.5" /> Validate All ({items.length})
          </Button>
        </div>
      )}

      {(phase === "review" || phase === "committing" || phase === "done") && (
        <div className="mt-5 pt-4 border-t border-slate-100 space-y-4">
          <div>
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Combined Diff vs Production</p>
            <div className="grid grid-cols-3 gap-3" data-testid="combined-diff-summary">
              <div className="text-center bg-emerald-50 rounded-lg py-3">
                <p className="text-xl font-bold text-emerald-700" data-testid="combined-diff-new">{combinedDiff.new_count}</p>
                <p className="text-xs text-slate-500">New</p>
              </div>
              <div className="text-center bg-sky-50 rounded-lg py-3">
                <p className="text-xl font-bold text-sky-700" data-testid="combined-diff-updated">{combinedDiff.updated_count}</p>
                <p className="text-xs text-slate-500">Updated</p>
              </div>
              <div className="text-center bg-slate-50 rounded-lg py-3">
                <p className="text-xl font-bold text-slate-500" data-testid="combined-diff-unchanged">{combinedDiff.unchanged_count}</p>
                <p className="text-xs text-slate-500">Unchanged</p>
              </div>
            </div>
            {failedCount > 0 && (
              <p className="text-xs text-red-600 mt-2 flex items-center gap-1" data-testid="validation-failed-count">
                <XCircle className="w-3.5 h-3.5" /> {failedCount} file(s) failed validation and will be skipped.
              </p>
            )}
          </div>

          {phase === "review" && readyCount > 0 && (
            <div className="space-y-3">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Source Tagging (stamped on commit, applied to all files)</p>
              <div className="flex flex-wrap items-end gap-4">
                <div>
                  <Label htmlFor="source_board" className="text-xs">Source Board</Label>
                  <Input id="source_board" value={sourceBoard} onChange={(e) => setSourceBoard(e.target.value)} className="w-40 mt-1" data-testid="source-board-input" />
                </div>
                <div>
                  <Label htmlFor="source_school" className="text-xs">Source School (optional)</Label>
                  <Input id="source_school" value={sourceSchool} onChange={(e) => setSourceSchool(e.target.value)} className="w-48 mt-1" placeholder="e.g. TFI" data-testid="source-school-input" />
                </div>
                <div className="flex items-center gap-2 pb-2">
                  <Checkbox id="is_shared" checked={isShared} onCheckedChange={setIsShared} data-testid="is-shared-checkbox" />
                  <Label htmlFor="is_shared" className="text-xs">Is Shared</Label>
                </div>
              </div>
              <Button onClick={commitAll} data-testid="commit-all-btn">
                <CheckCircle2 className="w-4 h-4 mr-1.5" /> Commit All ({readyCount})
              </Button>
            </div>
          )}

          {(phase === "committing" || phase === "done") && (
            <div className="flex items-center gap-2 text-xs text-slate-500">
              <Clock className="w-3.5 h-3.5" /> Committing sequentially — do not close this tab.
            </div>
          )}

          {phase === "done" && (
            <div className="bg-slate-50 rounded-lg p-4 flex items-center justify-between" data-testid="final-summary">
              <div className="text-sm text-slate-700">
                <strong>{items.length}</strong> total · <strong className="text-emerald-700">{committedCount}</strong> committed ·{" "}
                <strong className="text-red-600">{failedCount}</strong> failed
              </div>
              <Button variant="outline" size="sm" onClick={resetQueue} data-testid="clear-queue-btn">Clear &amp; Start Over</Button>
            </div>
          )}
        </div>
      )}
    </Card>
  );
}
